import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  keysApi,
  savedAssessmentsApi,
  type ApiKey,
  type SavedAssessment,
} from '../lib/api';
import { useUserPermissions } from '../hooks/useUserPermissions';

export default function SavedAssessmentsPage() {
  const { canWriteAssessments } = useUserPermissions();
  const [items, setItems] = useState<SavedAssessment[]>([]);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [viewing, setViewing] = useState<SavedAssessment | null>(null);

  const load = () => {
    setLoading(true);
    savedAssessmentsApi
      .list(filterKey || undefined)
      .then(setItems)
      .catch(() => setError('Failed to load saved assessments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    keysApi.list().then(setKeys).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this saved assessment?')) return;
    try {
      await savedAssessmentsApi.remove(id);
      setItems((prev) => prev.filter((x) => x._id !== id));
    } catch {
      setError('Failed to delete');
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Saved Assessments
        <Typography component="span" variant="body2" color="text.secondary" ml={1}>
          ({items.length} total)
        </Typography>
      </Typography>

      <Stack direction="row" spacing={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>API Key</InputLabel>
          <Select value={filterKey} label="API Key" onChange={(e) => setFilterKey(e.target.value)}>
            <MenuItem value="">All keys</MenuItem>
            {keys.map((k) => (
              <MenuItem key={k._id} value={k._id}>
                {k.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>States</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No saved assessments yet
                  </TableCell>
                </TableRow>
              ) : (
                items.map((it) => (
                  <TableRow key={it._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{it.name}</Typography>
                      {it.notes && (
                        <Typography variant="caption" color="text.secondary">
                          {it.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{it.apiKeyLabel ?? '—'}</TableCell>
                    <TableCell>
                      <code style={{ fontSize: 12 }}>{it.params?.entity}</code>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${it.params?.states?.length ?? 0} state${it.params?.states?.length === 1 ? '' : 's'}`}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(it.updatedAt).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View payload">
                        <IconButton size="small" onClick={() => setViewing(it)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canWriteAssessments && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(it._id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
        <DialogTitle>{viewing?.name}</DialogTitle>
        <DialogContent>
          {viewing?.notes && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {viewing.notes}
            </Typography>
          )}
          {viewing?.fiscalYearEnd && (
            <Typography variant="body2" mb={2}>
              <strong>Fiscal year end:</strong> {viewing.fiscalYearEnd.month}/{viewing.fiscalYearEnd.day}
            </Typography>
          )}
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: '#0f172a',
              color: '#e2e8f0',
              borderRadius: 1,
              fontSize: 12.5,
              fontFamily: 'monospace',
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(viewing?.params, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
